import { observer } from "mobx-react";
import { ITableColumn, TableModel } from "../../table/table.model";
import { Table } from "../../table/table";
import { useStore } from "app/context";
import { useEffect, useMemo } from "react";
import { DatenStandModel } from "app/stores/datenstand.store";

interface StoreEntry {
    store: string;
    dataType: string;
    aggStrategy: string;
}

interface DimensionGroupRow {
    dimensionKey: string;
    dimension: string;
    dimensionType: string;
    stores: string;
    storeCount: number;
    dataTypes: string;
    storeEntries: StoreEntry[];
}

function buildDimensionGroupRows(items: DatenStandModel[]): DimensionGroupRow[] {
    const dimMap = new Map<string, StoreEntry[]>();

    for (const item of items) {
        if (!item.analyticsStore || !item.dimensions) continue;
        const entry: StoreEntry = {
            store: item.analyticsStore,
            dataType: item.dataType,
            aggStrategy: item.aggStrategy || '',
        };
        for (const dim of item.dimensions) {
            const existing = dimMap.get(dim);
            if (existing) {
                existing.push(entry);
            } else {
                dimMap.set(dim, [entry]);
            }
        }
    }

    const rows: DimensionGroupRow[] = Array.from(dimMap.entries()).map(([dim, entries]) => {
        const parts = dim.split(':');
        return {
            dimensionKey: dim,
            dimension: parts[0] || dim,
            dimensionType: parts[1] || '',
            stores: entries.map(e => e.store).join(', '),
            storeCount: entries.length,
            dataTypes: entries.map(e => e.dataType).join(', '),
            storeEntries: entries,
        };
    });
    return rows;
}

interface AnalyticsStoresByDimensionListProps {
    searchFilter: string;
}

export const AnalyticsStoresByDimensionList = observer(({ searchFilter }: AnalyticsStoresByDimensionListProps) => {
    const { adminDatenStandStore: datenStandStore } = useStore();

    const tm = useMemo(() => {
        const tm = new TableModel<DimensionGroupRow, string>();
        const cols: ITableColumn<DimensionGroupRow, string>[] = [
            {
                label: 'Dimension',
                path: 'dimension',
            },
            {
                label: 'Type',
                path: 'dimensionType',
            },
            {
                label: 'Stores',
                path: 'storeCount',
            },
            {
                label: 'Analytics Stores',
                path: 'stores',
                render: (row) => {
                    const byAgg = new Map<string, StoreEntry[]>();
                    for (const e of row.data.storeEntries) {
                        const key = e.aggStrategy || '—';
                        const list = byAgg.get(key);
                        if (list) {
                            list.push(e);
                        } else {
                            byAgg.set(key, [e]);
                        }
                    }
                    return (
                        <div style={{ fontSize: '11px' }}>
                            {Array.from(byAgg.entries()).map(([agg, entries]) => (
                                <div key={agg} style={{ marginBottom: '4px' }}>
                                    <strong>{agg}</strong>
                                    <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                        {entries.map((e, i) => <li key={i}>{e.store}</li>)}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    );
                },
            },
            {
                label: 'Data Types',
                path: 'dataTypes',
                render: (row) => (
                    <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '11px' }}>
                        {row.data.storeEntries.map((e, i) => <li key={i}>{e.dataType}</li>)}
                    </ul>
                ),
            },
        ];

        tm.setCols(cols);
        tm.sortBy = 'storeCount';
        tm.sortAsc = false;
        tm.idProperty = 'dimensionKey';
        tm.idType = 'string';
        return tm;
    }, []);

    useEffect(() => {
        datenStandStore.findAll().then(res => {
            let rows = buildDimensionGroupRows(res);

            if (searchFilter) {
                const filter = searchFilter.toLowerCase();
                rows = rows.filter(r =>
                    r.dimension.toLowerCase().includes(filter) ||
                    r.dimensionType.toLowerCase().includes(filter) ||
                    r.stores.toLowerCase().includes(filter) ||
                    r.dataTypes.toLowerCase().includes(filter)
                );
            }

            tm.setRowData(rows);
        });
    }, [datenStandStore, searchFilter]);

    return (
        <div className="table-container">
            <Table stickyHeader={true} tm={tm} />
        </div>
    );
});
